import { useCallback, useEffect, useState } from 'react';
import type { TextBlock } from '@/features/drawing/model';
import { TEXT_BLOCK_CONSTRAINTS } from './constants';
import type { ResizingState, ResizeHandle, CanvasTransform } from './types';

interface UseTextBlockResizeProps {
  textBlocks: TextBlock[];
  canvasTransform: CanvasTransform;
  updateTextBlock: (
    id: string,
    updates: Partial<
      Pick<TextBlock, 'x' | 'y' | 'width' | 'height' | 'isManualWidth'>
    >
  ) => void;
}

interface UseTextBlockResizeReturn {
  resizing: ResizingState | null;
  handleResizeStart: (
    e: React.MouseEvent,
    blockId: string,
    handle: ResizeHandle
  ) => void;
  handleTouchResizeStart: (
    e: React.TouchEvent,
    blockId: string,
    handle: ResizeHandle
  ) => void;
  clearResize: () => void;
}

// Calculate new dimensions based on resize handle and delta
function calculateResizeDimensions(
  handle: ResizeHandle,
  dx: number,
  dy: number,
  startWidth: number,
  startHeight: number,
  startBlockX: number,
  startBlockY: number
): { newWidth: number; newHeight: number; newX: number; newY: number } {
  const { MIN_WIDTH, MIN_HEIGHT } = TEXT_BLOCK_CONSTRAINTS;

  let newWidth = startWidth;
  let newHeight = startHeight;
  let newX = startBlockX;
  let newY = startBlockY;

  switch (handle) {
    case 'nw':
      newWidth = Math.max(MIN_WIDTH, startWidth - dx);
      newHeight = Math.max(MIN_HEIGHT, startHeight - dy);
      newX = startBlockX + (startWidth - newWidth);
      newY = startBlockY + (startHeight - newHeight);
      break;
    case 'n':
      newHeight = Math.max(MIN_HEIGHT, startHeight - dy);
      newY = startBlockY + (startHeight - newHeight);
      break;
    case 'ne':
      newWidth = Math.max(MIN_WIDTH, startWidth + dx);
      newHeight = Math.max(MIN_HEIGHT, startHeight - dy);
      newY = startBlockY + (startHeight - newHeight);
      break;
    case 'e':
      newWidth = Math.max(MIN_WIDTH, startWidth + dx);
      break;
    case 'se':
      newWidth = Math.max(MIN_WIDTH, startWidth + dx);
      newHeight = Math.max(MIN_HEIGHT, startHeight + dy);
      break;
    case 's':
      newHeight = Math.max(MIN_HEIGHT, startHeight + dy);
      break;
    case 'sw':
      newWidth = Math.max(MIN_WIDTH, startWidth - dx);
      newHeight = Math.max(MIN_HEIGHT, startHeight + dy);
      newX = startBlockX + (startWidth - newWidth);
      break;
    case 'w':
      newWidth = Math.max(MIN_WIDTH, startWidth - dx);
      newX = startBlockX + (startWidth - newWidth);
      break;
  }

  return { newWidth, newHeight, newX, newY };
}

export function useTextBlockResize({
  textBlocks,
  canvasTransform,
  updateTextBlock,
}: UseTextBlockResizeProps): UseTextBlockResizeReturn {
  const [resizing, setResizing] = useState<ResizingState | null>(null);

  // Handle resize start (mouse)
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, blockId: string, handle: ResizeHandle) => {
      e.stopPropagation();
      const block = textBlocks.find((b) => b.id === blockId);
      if (!block) return;

      // Switch to manual width mode when user starts resizing
      if (!block.isManualWidth) {
        updateTextBlock(blockId, { isManualWidth: true });
      }

      setResizing({
        blockId,
        handle,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: block.width,
        startHeight: block.height,
        startBlockX: block.x,
        startBlockY: block.y,
      });
    },
    [textBlocks, updateTextBlock]
  );

  // Handle resize start (touch)
  const handleTouchResizeStart = useCallback(
    (e: React.TouchEvent, blockId: string, handle: ResizeHandle) => {
      e.stopPropagation();
      const block = textBlocks.find((b) => b.id === blockId);
      if (!block) return;

      // Switch to manual width mode when user starts resizing
      if (!block.isManualWidth) {
        updateTextBlock(blockId, { isManualWidth: true });
      }

      const touch = e.touches[0];
      if (!touch) return;

      setResizing({
        blockId,
        handle,
        startX: touch.clientX,
        startY: touch.clientY,
        startWidth: block.width,
        startHeight: block.height,
        startBlockX: block.x,
        startBlockY: block.y,
      });
    },
    [textBlocks, updateTextBlock]
  );

  // Handle mouse move for resizing
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!resizing) return;

      const dx = (e.clientX - resizing.startX) / canvasTransform.zoom;
      const dy = (e.clientY - resizing.startY) / canvasTransform.zoom;

      const { newWidth, newHeight, newX, newY } = calculateResizeDimensions(
        resizing.handle,
        dx,
        dy,
        resizing.startWidth,
        resizing.startHeight,
        resizing.startBlockX,
        resizing.startBlockY
      );

      updateTextBlock(resizing.blockId, {
        width: newWidth,
        height: newHeight,
        x: newX,
        y: newY,
      });
    },
    [resizing, canvasTransform.zoom, updateTextBlock]
  );

  // Handle touch move for resizing
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!resizing) return;

      const touch = e.touches[0];
      if (!touch) return;

      const dx = (touch.clientX - resizing.startX) / canvasTransform.zoom;
      const dy = (touch.clientY - resizing.startY) / canvasTransform.zoom;

      const { newWidth, newHeight, newX, newY } = calculateResizeDimensions(
        resizing.handle,
        dx,
        dy,
        resizing.startWidth,
        resizing.startHeight,
        resizing.startBlockX,
        resizing.startBlockY
      );

      updateTextBlock(resizing.blockId, {
        width: newWidth,
        height: newHeight,
        x: newX,
        y: newY,
      });
    },
    [resizing, canvasTransform.zoom, updateTextBlock]
  );

  // Clear resize state
  const clearResize = useCallback(() => {
    setResizing(null);
  }, []);

  // Add global event listeners for resizing
  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', clearResize);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', clearResize);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', clearResize);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', clearResize);
      };
    }
    return undefined;
  }, [resizing, handleMouseMove, handleTouchMove, clearResize]);

  return {
    resizing,
    handleResizeStart,
    handleTouchResizeStart,
    clearResize,
  };
}
